import { Component, CUSTOM_ELEMENTS_SCHEMA, Input, ChangeDetectionStrategy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MasterDataManagementService } from '../../../services/master-data-management.service';
import { AemsMultilineTagInputComponent } from '../../../shared-components/aems-multiline-tag-input/aems-multiline-tag-input.component';
import { RouterModule } from '@angular/router';
import '@maersk-global/mds-components-core/mc-multi-select';
import '@maersk-global/mds-components-core/mc-input-date';
import { LabourRatesFilterForm } from '../../../form-model/labour-rates-filter.form.model';
import { firstValueFrom, ReplaySubject, first } from 'rxjs';
import { ApplicationtTextService } from '../../../services/application-text.service';
import { ToastrService } from '../../../services/toastr-service';
import { LabourRatesHeader } from '../../../models/labour-rates-header.model';
import { LabourRatesFilterGrid } from '../../../models/labour-rates-filter.model';
import { CodeDescription } from '../../../models/code-description.model';
import { DateFilterRowComponent } from '../../../shared-components/date-filter-row/date-filter-row.component';
@Component({
  selector: 'app-labour-rates-advance-filter',
  imports: [FormsModule, ReactiveFormsModule, RouterModule, AemsMultilineTagInputComponent, DateFilterRowComponent],
  templateUrl: './labour-rates-advance-filter.component.html',
  styleUrl: './labour-rates-advance-filter.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class AdvanceFilterComponent implements OnInit {
  expiryDateTypes = [
    { value: 'EQUALS', label: 'Equals' },
    { value: 'ON_OR_BEFORE', label: 'On or Before' },
    { value: 'ON_OR_AFTER', label: 'On or After' },
    { value: 'BETWEEN', label: 'Between' },
    { value: 'BEFORE', label: 'Before' },
    { value: 'AFTER', label: 'After' },
  ];

  effectiveDateTypes = [
    { value: 'EQUALS', label: 'Equals' },
    { value: 'ON_OR_BEFORE', label: 'On or Before' },
    { value: 'ON_OR_AFTER', label: 'On or After' },
    { value: 'BETWEEN', label: 'Between' },
    { value: 'BEFORE', label: 'Before' },
    { value: 'AFTER', label: 'After' },
  ];
  @Input({ required: true }) labourRatesFilterForm!: FormGroup<LabourRatesFilterForm>;
  shopCodeRegex: RegExp = /\b.{3}\b/g;
  labourRatesData: LabourRatesHeader[] = [];
  equipmentTypeOptions: CodeDescription[] = [
    { code: 'chas', description: 'CHAS - Chassis' },
    { code: 'cont', description: 'CONT - Container' },
    { code: 'gens', description: 'GENS - Genset' },
    { code: 'pwpk', description: 'PWPK - Power Pack' },
    { code: 'reef', description: 'REEF - Reefer Box' },
    { code: 'refu', description: 'REFU - Reefer Unit' },
  ];
  expiryDateOptions: string[] = [];
  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  constructor(
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private masterDataManagementService: MasterDataManagementService,
    private appTextService: ApplicationtTextService,
  ) {}

  ngOnInit() {
    this.addControlsToTheFormGroup();
    this.subscribeToMasterDataManagementApiCalls();
    this.loadLabourRates();
  }

  isControlInErrorState(control: FormControl): boolean | null {
    if (control.invalid && control.touched) {
      return true;
    }
    return null;
  }

  private addControlsToTheFormGroup() {
    this.labourRatesFilterForm.addControl('shopCode', this.formBuilder.control<string[] | null>(null));
    this.labourRatesFilterForm.addControl('equipmentType', this.formBuilder.control<CodeDescription[] | null>(null));
    this.labourRatesFilterForm.addControl('expiryDateType', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('expiryDate', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('expiryDateFrom', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('expiryDateTo', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('effectiveDateType', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('effectiveDate', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('effectiveDateFrom', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('effectiveDateTo', this.formBuilder.control<string | null>(null));
    this.labourRatesFilterForm.addControl('applyLabourRatesFilter', this.formBuilder.control<boolean | null>(null));
  }

  private subscribeToMasterDataManagementApiCalls() {}

  async loadLabourRates(filters?: LabourRatesFilterGrid) {
    try {
      const response = await firstValueFrom(this.masterDataManagementService.getLabourRate(1, 100, undefined, filters).pipe(first()));
      this.labourRatesData = response.body ?? [];
      const options = this.masterDataManagementService.extractFilterOptions(this.labourRatesData);
      this.expiryDateOptions = options.expiryDates;
    } catch (error) {
      // Failed to load labour rates
    }
  }

  applyFilters() {
    const rawFilters = this.labourRatesFilterForm.value;
    const expiryDateType = rawFilters.expiryDateType ?? '';

    const filters: LabourRatesFilterGrid = { expiryDateType };

    if (expiryDateType === 'BETWEEN') {
      if (rawFilters.expiryDateFrom) filters.expiryDateFrom = rawFilters.expiryDateFrom;
      if (rawFilters.expiryDateTo) filters.expiryDateTo = rawFilters.expiryDateTo;
    } else if (['EQUALS', 'BEFORE', 'AFTER', 'ON_OR_BEFORE', 'ON_OR_AFTER'].includes(expiryDateType)) {
      if (rawFilters.expiryDate) filters.expiryDate = rawFilters.expiryDate;
    }
    this.labourRatesFilterForm.get('applyLabourRatesFilter')?.setValue(true);
  }

  clearFilters() {
    this.labourRatesFilterForm.reset();
  }
}
